import React from "react";
import styled from "styled-components";
import {
  UtensilsCrossed,
  ShoppingBag,
  Store,
  TreePine,
  KeyRound,
  Info,
  Compass,
  Clock,
  BedDouble,
} from "lucide-react";
import { ServiceCard } from "../../../../components/service-card";
import { LanguageSwitcher } from "../../../../components/language-switcher";
import { NotificationToggle } from "../../../../components/notification-toggle";
import { RoomInputModal } from "../../../../components/room-input-modal";
import { KitchenScheduleModal } from "../../../../components/kitchen-schedule-modal";
import { useLanguage } from "../../../../contexts/language";
import { useRoom } from "../../../../contexts/room";
import { getKitchenStatus } from "../fnb/mock";
import { unmodule } from "../../../../util";

const Image = (props) => {
  return <img {...props} loading="lazy" />;
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
`;

const Header = styled.header`
  border-bottom: 1px solid rgba(229, 229, 229, 0.25);
  background-color: #fff;
`;

const HeaderContent = styled.div`
  margin: 0 auto;
  display: flex;
  height: 64px;
  max-width: 768px;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoImage = styled(Image)`
  height: 40px;
  width: 40px;
  object-fit: contain;
`;

const BrandInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BrandName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const BrandSubtitle = styled.span`
  font-size: 13px;
  color: #4b5563;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuButton = styled.button`
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  background-color: #f9f9f9;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: rgba(201, 169, 97, 0.1);
  }
`;

const WelcomeSection = styled.section`
  background: linear-gradient(to bottom, #fff, #f9f9f9);
  padding: 32px 16px;
`;

const WelcomeContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
`;

const WelcomeTitle = styled.h1`
  font-size: 24px;
  font-weight: 300;
  color: #333;
  margin: 0;
`;

const GuestNameTitle = styled.h2`
  margin-top: 4px;
  font-size: 30px;
  font-weight: 600;
  color: #333;
  margin-bottom: 0;
`;

const RoomInfo = styled.p`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #c9a961;
`;

const WelcomeDescription = styled.p`
  margin-top: 16px;
  font-size: 14px;
  color: #4b5563;
`;

const ServicesSection = styled.section`
  padding: 32px 16px;
`;

const ServicesContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const QuickAccessSection = styled.section`
  padding: 24px 16px;
`;

const QuickAccessContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
`;

const QuickAccessBanner = styled.div`
  border-radius: 16px;
  border: 1px solid rgba(201, 169, 97, 0.2);
  background: linear-gradient(
    to right,
    rgba(201, 169, 97, 0.05),
    rgba(201, 169, 97, 0.1)
  );
  padding: 24px;
  backdrop-filter: blur(8px);
`;

const QuickAccessInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const QuickAccessText = styled.div`
  flex: 1;
`;

const QuickAccessTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const QuickAccessDescription = styled.p`
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
`;

const QuickAccessButton = styled.button`
  border-radius: 8px;
  background-color: #c9a961;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;

  &:hover {
    background-color: rgba(201, 169, 97, 0.8);
    box-shadow: 0 4px 12px rgba(201, 169, 97, 0.3);
  }
`;

const Footer = styled.footer`
  margin-top: 48px;
  border-top: 1px solid #e5e5e5;
  background-color: #fff;
  padding: 24px 16px;
`;

const FooterContent = styled.div`
  margin: 0 auto;
  max-width: 768px;
  text-align: center;
`;

const FooterText = styled.p`
  font-size: 13px;
  color: #4b5563;
  margin: 0;

  &:not(:first-child) {
    margin-top: 4px;
  }
`;

const FnbCardWrapper = styled.div`
  position: relative;
`;

const StatusDotIndicator = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 3px
      ${(props) =>
        props.$isOpen ? "rgba(15,122,57,0.25)" : "rgba(161,21,45,0.25)"};
  z-index: 1;
`;

const ScheduleIconButton = styled.button`
  position: absolute;
  top: 8px;
  left: 8px;
  border: none;
  background: rgba(201, 169, 97, 0.1);
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #c9a961;
  z-index: 1;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(201, 169, 97, 0.25);
  }

  &:hover > span {
    opacity: 1;
    visibility: visible;
  }
`;

const ScheduleTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  background: #333;
  color: #fff;
  font-size: 10px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.15s,
    visibility 0.15s;
  pointer-events: none;
`;

const FnbBottomLabel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 0 0 12px 12px;
  background-color: ${(props) => (props.$isOpen ? "#e6f4ea" : "#fce8e6")};
  color: ${(props) => (props.$isOpen ? "#0f7a39" : "#a1152d")};
`;

const UnnamedComponent = (props) => {
  const { building_code, room_no } = props;
  const { t, language } = useLanguage();
  const { roomNumber, guestName, loadCachedProfile, setGuestProfile } =
    useRoom();
  const [showRoomModal, setShowRoomModal] = React.useState(false);
  const [kitchenStatus, setKitchenStatus] = React.useState(null);
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);

  React.useEffect(() => {
    const hydrateProfile = async () => {
      const cachedLoaded = loadCachedProfile(building_code);
      if (cachedLoaded || guestName) return;

      if (room_no) {
        const verified = await setGuestProfile({
          name: "",
          room: room_no,
          buildingCode: building_code,
        });
        if (!verified) {
          setShowRoomModal(true);
        }
        return;
      }

      setShowRoomModal(true);
    };

    hydrateProfile();
  }, [building_code, guestName, room_no, loadCachedProfile, setGuestProfile]);

  React.useEffect(() => {
    getKitchenStatus().then((status) => {
      if (status) setKitchenStatus(status);
    });
  }, []);

  const getKitchenSubtitle = () => {
    if (!kitchenStatus) return "";
    if (kitchenStatus.is_open && kitchenStatus.current_slot_close) {
      return `${t.kitchen.closesAt} ${kitchenStatus.current_slot_close}`;
    }
    if (!kitchenStatus.is_open && kitchenStatus.next_opening) {
      const next = kitchenStatus.next_opening;
      const dayName = language === "id" ? next.day_id : next.day_en;
      const isSameDay = next.day_of_week === kitchenStatus.server_day;
      if (isSameDay) return `${t.kitchen.opensAt} ${next.open_time}`;
      return `${t.kitchen.opensAt} ${dayName} ${next.open_time}`;
    }
    return t.kitchen.closedToday;
  };

  const services = [
    {
      title: t.services.selfDeposit.title,
      icon: KeyRound,
      href: `/hebe/${building_code}/hgs/bookings`,
      description: t.services.selfDeposit.description,
    },
    {
      title: t.services.myOrder.title,
      icon: ShoppingBag,
      href: `/hebe/${building_code}/hgs/orders`,
      description: t.services.myOrder.description,
    },
    {
      title: t.services.foodBeverages.title,
      icon: UtensilsCrossed,
      href: `/hebe/${building_code}/hgs/fnb`,
      description: t.services.foodBeverages.description,
    },
    {
      title: t.services.roomServices.title,
      icon: BedDouble,
      href: `/hebe/${building_code}/hgs/add-on-facility`,
      description: t.services.roomServices.description,
    },
    {
      title: t.services.activities.title,
      icon: Compass,
      // href: `/hebe/${building_code}/hgs/activities`,
      href: "#",
      description: t.services.activities.description,
    },
    {
      title: t.services.ecoTours.title,
      icon: TreePine,
      // href: `/hebe/${building_code}/hgs/eco-tours`,
      href: "#",
      description: t.services.ecoTours.description,
    },
    {
      title: t.services.lifestyle.title,
      icon: Store,
      // href: `/hebe/${building_code}/hgs/lifestyle`,
      href: "#",
      description: t.services.lifestyle.description,
    },
    {
      title: t.services.info.title,
      icon: Info,
      // href: `/hebe/${building_code}/hgs/info`,
      href: "#",
      description: t.services.info.description,
    },
  ];

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <LogoSection>
            <LogoImage
              src={unmodule(
                require("../../../../assets/hebe-logo-vertical.png"),
              )}
              alt="HEBE Logo"
              width={40}
              height={40}
            />
            <BrandInfo>
              <BrandName>{t.brandName}</BrandName>
              <BrandSubtitle>{t.brandSubtitle}</BrandSubtitle>
            </BrandInfo>
          </LogoSection>
          <HeaderActions>
            <NotificationToggle />
            <LanguageSwitcher />
            <MenuButton onClick={() => setShowRoomModal(true)}>
              {t.header.changeProfile}
            </MenuButton>
          </HeaderActions>
        </HeaderContent>
      </Header>

      <WelcomeSection>
        <WelcomeContent>
          <WelcomeTitle>{t.welcomeTo}</WelcomeTitle>
          <GuestNameTitle>{guestName || t.guestName}</GuestNameTitle>
          {roomNumber && (
            <RoomInfo>
              {t.room.roomNumber}: {roomNumber}
            </RoomInfo>
          )}
          <WelcomeDescription>{t.welcomeDescription}</WelcomeDescription>
        </WelcomeContent>
      </WelcomeSection>

      <ServicesSection>
        <ServicesContent>
          <ServicesGrid>
            {services.map((service) => {
              const isFnb = service.title === t.services.foodBeverages.title;
              if (isFnb && kitchenStatus) {
                const isOpen = kitchenStatus.is_open;
                const bottomText = isOpen
                  ? kitchenStatus.current_slot_close
                    ? `${t.kitchen.closesAt} ${kitchenStatus.current_slot_close}`
                    : t.kitchen.open
                  : getKitchenSubtitle();
                return (
                  <FnbCardWrapper key={service.title}>
                    <StatusDotIndicator $isOpen={isOpen} />
                    <ScheduleIconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowScheduleModal(true);
                      }}
                    >
                      <Clock size={14} />
                      <ScheduleTooltip>
                        {t.kitchen.viewSchedule}
                      </ScheduleTooltip>
                    </ScheduleIconButton>
                    <ServiceCard {...service} />
                    <FnbBottomLabel $isOpen={isOpen}>
                      {bottomText}
                    </FnbBottomLabel>
                  </FnbCardWrapper>
                );
              }
              return <ServiceCard key={service.title} {...service} />;
            })}
          </ServicesGrid>
        </ServicesContent>
      </ServicesSection>

      <QuickAccessSection>
        <QuickAccessContent>
          <QuickAccessBanner>
            <QuickAccessInner>
              <QuickAccessText>
                <QuickAccessTitle>{t.quickAccess.title}</QuickAccessTitle>
                <QuickAccessDescription>
                  {t.quickAccess.description}
                </QuickAccessDescription>
              </QuickAccessText>
              <QuickAccessButton>{t.quickAccess.button}</QuickAccessButton>
            </QuickAccessInner>
          </QuickAccessBanner>
        </QuickAccessContent>
      </QuickAccessSection>

      <Footer>
        <FooterContent>
          <FooterText>{t.footer.copyright}</FooterText>
          <FooterText>{t.footer.tagline}</FooterText>
        </FooterContent>
      </Footer>

      {showRoomModal && (
        <RoomInputModal
          onRoomVerified={() => setShowRoomModal(false)}
          buildingCode={building_code}
        />
      )}
      {showScheduleModal && (
        <KitchenScheduleModal
          kitchenStatus={kitchenStatus}
          onClose={() => setShowScheduleModal(false)}
        />
      )}
    </PageContainer>
  );
};

export default UnnamedComponent;
